import { ChargeDetailModal } from '@/components/enrollments/charge-detail-modal';
import { ChargeExceptionModal } from '@/components/enrollments/charge-exception-modal';
import { ContractFormScreen } from '@/components/enrollments/contract-form-screen';
import { EnrollmentChargeTab } from '@/components/enrollments/enrollment-charge-tab';
import { EnrollmentDetailsTab } from '@/components/enrollments/enrollment-details-tab';
import { EnrollmentServiceTab } from '@/components/enrollments/enrollment-service-tab';
import { ManualChargeModal } from '@/components/charges/manual-charge-modal';
import { ServiceTabHeader, TabKey } from '@/components/services/service-tab-header';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { DetailHeader } from '@/components/ui/detail-header';
import api from '@/services/api';
import { getApiErrorMessage } from '@/utils/api-error';
import { useToastStore } from '@/store/toastStore';
import { useQueryClient } from '@tanstack/react-query';
import { chargeKeys } from '@/hooks/use-charges';
import { Charge } from '@/types/charge';
import { ChargeException } from '@/types/charge-exception';
import { Enrollments } from '@/types/enrollments';
import { Ionicons } from '@expo/vector-icons';
import { router, useLocalSearchParams } from 'expo-router';
import React, { useCallback, useEffect, useState } from 'react';
import { DashboardScrollView } from '@/components/dashboard-scroll-view';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ActivityIndicator, View } from 'react-native';
import { useDeleteChargeException } from '@/hooks/use-charge-exceptions';
import { useDeleteEnrollment } from '@/hooks/use-enrollments';

export type TabConfig = {
  key: TabKey;
  title: string;
};

const tabs: TabConfig[] = [
  { key: 'details', title: 'Contrato' },
  { key: 'charge', title: 'Cobrança' },
  { key: 'service', title: 'Serviço' },
];

/**
 * Ver um contrato. Criar e editar vivem em `ContractFormScreen` (mesma tela C3,
 * só muda o modo) — aqui só o "Editar" navega para lá via `?edit=1`.
 */
export default function EnrollmentDetailScreen() {
  const params = useLocalSearchParams<{
    id?: string;
    serviceId?: string;
    edit?: string;
  }>();
  const enrollmentId = Array.isArray(params.id) ? params.id[0] : params.id;
  const serviceIdParam = Array.isArray(params.serviceId) ? params.serviceId[0] : params.serviceId;
  const editParam = Array.isArray(params.edit) ? params.edit[0] : params.edit;

  const isCreating = enrollmentId === 'new';
  const isEditing = editParam === '1';

  const [enrollment, setEnrollment] = useState<Enrollments | null>(null);
  const [exceptionToDelete, setExceptionToDelete] = useState<ChargeException | null>(null);
  const [loading, setLoading] = useState(!isCreating);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('details');
  const [initialServiceName, setInitialServiceName] = useState<string | undefined>();
  const { addToast } = useToastStore();
  const deleteEnrollmentMutation = useDeleteEnrollment();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [manualChargeOpen, setManualChargeOpen] = useState(false);
  const [exceptionModalOpen, setExceptionModalOpen] = useState(false);
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [exceptionModalMode, setExceptionModalMode] = useState<'create' | 'edit'>('create');
  const [exceptionEditing, setExceptionEditing] = useState<ChargeException | null>(null);
  const [exceptionDefaultDate, setExceptionDefaultDate] = useState<Date | undefined>(undefined);
  const queryClient = useQueryClient();
  const [selectedCharge, setSelectedCharge] = useState<Charge | null>(null);
  const deleteExceptionMutation = useDeleteChargeException(enrollmentId);

  const handleChargePress = (charge: Charge) => {
    setSelectedCharge(charge);
    setIsModalVisible(true);
  };

  const handleChargeUpdate = () => {
    if (enrollmentId) {
      queryClient.invalidateQueries({ queryKey: chargeKeys.byEnrollment(enrollmentId) });
      fetchEnrollment();
    }
  };

  const handleCloseModal = useCallback(() => {
    setIsModalVisible(false);
    setSelectedCharge(null);
  }, []);

  const openManualChargeModal = () => setManualChargeOpen(true);
  const handleManualChargeSuccess = () => fetchEnrollment();

  const openExceptionModal = (
    mode: 'create' | 'edit',
    exception?: ChargeException | null,
    defaultDate?: Date,
  ) => {
    setExceptionModalMode(mode);
    setExceptionEditing(exception || null);
    setExceptionDefaultDate(defaultDate);
    setExceptionModalOpen(true);
  };

  const handleDeleteException = (exception: ChargeException) => setExceptionToDelete(exception);

  const deleteException = async () => {
    if (!exceptionToDelete) return;
    try {
      await deleteExceptionMutation.mutateAsync(exceptionToDelete.id);
      addToast('Exceção removida.', 'success');
      fetchEnrollment();
    } catch (err: any) {
      addToast(getApiErrorMessage(err, 'Não foi possível remover a exceção.'), 'error');
    }
  };

  const handleTabChange = useCallback((tabKey: TabKey) => {
    setActiveTab(tabKey);
  }, []);

  const applyEnrollment = useCallback((data: Enrollments | null) => {
    setEnrollment(data);
    setInitialServiceName(data?.service?.name);
    setActiveTab('details');
  }, []);

  const fetchEnrollment = useCallback(async () => {
    if (isCreating || !enrollmentId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<Enrollments>(`enrollments/${enrollmentId}`);
      applyEnrollment(response.data);
    } catch (err) {
      setError('Não foi possível carregar os detalhes do contrato.');
      console.error('Fetch enrollment error:', err);
      applyEnrollment(null);
    } finally {
      setLoading(false);
    }
  }, [enrollmentId, isCreating, applyEnrollment]);

  useEffect(() => {
    setError(null);
    if (isCreating || isEditing) return;
    setEnrollment(null);
    setInitialServiceName(undefined);
    fetchEnrollment();
  }, [enrollmentId, isCreating, isEditing, fetchEnrollment]);

  // Criar e editar: mesma tela C3, só muda o modo.
  if (isCreating) {
    return <ContractFormScreen serviceIdParam={serviceIdParam} />;
  }
  if (isEditing && enrollmentId) {
    return <ContractFormScreen enrollmentId={enrollmentId} serviceIdParam={serviceIdParam} />;
  }

  const handleBack = () => {
    if (serviceIdParam) {
      router.replace(`/(tabs)/(provider)/services/${serviceIdParam}` as any);
    } else {
      router.replace('/(tabs)/(provider)/enrollments' as any);
    }
  };

  const handleEdit = () => {
    router.setParams({ edit: '1' });
  };

  const handleOpenDeleteConfirm = () => {
    if (!enrollmentId) return;
    setDeleteConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!enrollmentId) return;
    try {
      await deleteEnrollmentMutation.mutateAsync(enrollmentId);
      setDeleteConfirmModalOpen(false);
      addToast('Contrato excluído.', 'success');
      if (serviceIdParam) {
        router.replace(`/(tabs)/(provider)/services/${serviceIdParam}` as any);
      } else {
        router.replace('/(tabs)/(provider)/enrollments' as any);
      }
    } catch (e: any) {
      addToast(getApiErrorMessage(e, 'Erro ao excluir contrato.'), 'error');
    }
  };

  if (loading) {
    return (
      <View className="flex-1 items-center justify-center bg-canvas">
        <ActivityIndicator size="large" className="text-primary" />
        <ThemedText className="mt-2 text-foreground">Carregando detalhes do contrato...</ThemedText>
      </View>
    );
  }

  if (error) {
    return (
      <View className="flex-1 items-center justify-center bg-canvas p-5">
        <Ionicons
          name="alert-circle-outline"
          size={48}
          className="mb-4 text-error-600"
        />
        <ThemedText className="mb-4 text-center text-foreground">{error}</ThemedText>
        <Button title={'Voltar'} onPress={handleBack} variant={'link'}></Button>
      </View>
    );
  }

  if (!enrollment) {
    return (
      <View className="flex-1 items-center justify-center bg-canvas p-5">
        <ThemedText className="mb-4 text-center text-foreground">
          Contrato não encontrado.
        </ThemedText>
        <Button title="Voltar" onPress={handleBack} variant="link"></Button>
      </View>
    );
  }

  return (
    <>
      <Dialog open={deleteConfirmModalOpen} onOpenChange={setDeleteConfirmModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir contrato</DialogTitle>
            <DialogDescription>
              Tem certeza? Isso removerá o contrato, todas as cobranças e o agendamento de serviço.
              Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6 flex-row gap-3">
            <Button
              title="Cancelar"
              variant="outline"
              onPress={() => setDeleteConfirmModalOpen(false)}
              className="flex-1"
            />
            <Button
              title="Excluir"
              variant="destructive"
              onPress={handleConfirmDelete}
              disabled={deleteEnrollmentMutation.isPending}
              className="flex-1"
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <View className="w-full flex-1 bg-canvas pt-5">
        <DetailHeader
          className="mb-4 mt-5"
          title={enrollment?.client?.user?.username || 'Detalhes do Contrato'}
          subtitle={enrollment?.service?.name}
          onBack={handleBack}
          actions={<Button title="Editar" size="sm" onPress={handleEdit} />}
        />
        <ServiceTabHeader
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          isEditing={false}
          disableTabNavigation={false}
        />
        <DashboardScrollView
          key={enrollmentId}
          className="mt-3 w-full flex-1 bg-canvas"
          contentContainerStyle={{ alignItems: 'flex-start', paddingBottom: 80 }}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {activeTab === 'details' && (
            <EnrollmentDetailsTab
              enrollment={enrollment}
              initialServiceName={initialServiceName}
            />
          )}
          {activeTab === 'charge' && (
            <EnrollmentChargeTab
              enrollment={enrollment}
              onChargePress={handleChargePress}
              onCreateManualCharge={openManualChargeModal}
              onAddException={(date) => openExceptionModal('create', null, date || undefined)}
              onEditException={(exception) => openExceptionModal('edit', exception)}
              onDeleteException={handleDeleteException}
            />
          )}
          {activeTab === 'service' && (
            <EnrollmentServiceTab
              enrollment={enrollment}
              initialServiceName={initialServiceName}
            />
          )}
          {activeTab === 'details' && enrollment ? (
            <View className="mt-4 w-full px-5">
              <Button
                variant="outline"
                onPress={handleOpenDeleteConfirm}
                disabled={deleteEnrollmentMutation.isPending}
                className="w-full border-error-500/30"
              >
                <ThemedText className="font-semibold text-sm text-error-600">
                  Excluir contrato
                </ThemedText>
              </Button>
            </View>
          ) : null}
          <View className="h-20" />
        </DashboardScrollView>
        <ChargeDetailModal
          visible={isModalVisible}
          charge={selectedCharge}
          onClose={handleCloseModal}
          onUpdate={handleChargeUpdate}
        />
        <ManualChargeModal
          open={manualChargeOpen}
          onOpenChange={setManualChargeOpen}
          enrollmentId={enrollment?.id}
          enrollmentLabel={
            enrollment
              ? `${enrollment.service?.name || 'Serviço'} — ${
                  enrollment.client?.user?.username || 'Cliente'
                }`
              : undefined
          }
          onSuccess={handleManualChargeSuccess}
        />
        {enrollment?.id && (
          <ChargeExceptionModal
            open={exceptionModalOpen}
            onOpenChange={setExceptionModalOpen}
            enrollmentId={enrollment.id}
            mode={exceptionModalMode}
            exception={exceptionEditing}
            defaultDate={exceptionDefaultDate}
            onSuccess={fetchEnrollment}
          />
        )}
      </View>

      <ConfirmDialog
        open={exceptionToDelete !== null}
        onOpenChange={(open) => !open && setExceptionToDelete(null)}
        title="Excluir exceção"
        message="Tem certeza que deseja remover esta exceção de cobrança?"
        confirmLabel="Excluir"
        destructive
        onConfirm={deleteException}
      />
    </>
  );
}
